import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { agenciesApi, workspacesApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { Loader2, ShieldCheck } from 'lucide-react';

export function SettingsGeneral() {
  const { getToken } = useAuth();
  const { currentAgency, currentWorkspace, setCurrentAgency } = useAppStore();
  const qc = useQueryClient();

  const [name, setName] = useState(currentAgency?.name ?? '');
  // Re-sync when the agency changes, so switching agencies never saves the
  // previous agency's name onto the new one.
  useEffect(() => setName(currentAgency?.name ?? ''), [currentAgency?.id]);

  const update = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return agenciesApi.update(token!, currentAgency!.id, { name });
    },
    onSuccess: (data) => {
      setCurrentAgency(data.agency);
      qc.invalidateQueries({ queryKey: ['agencies'] });
    },
  });

  // Workspace approval policy — read live so the toggle reflects the DB.
  const { data: wsData } = useQuery({
    queryKey: ['workspace', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.get(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });
  const requireApproval = !!wsData?.workspace?.require_approval;

  const toggleApproval = useMutation({
    mutationFn: async (value: boolean) => {
      const token = await getToken();
      return workspacesApi.update(token!, currentWorkspace!.id, {
        require_approval: value,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', currentWorkspace?.id] }),
  });

  if (!currentAgency) {
    return <p className="text-sm text-muted-foreground">No agency selected</p>;
  }

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-1">General Settings</h2>
        <p className="text-xs text-muted-foreground">Manage your agency settings</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-foreground block mb-1.5">
            Agency Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">
            Agency ID
          </label>
          <p className="text-xs text-muted-foreground font-mono bg-muted px-3 py-2 rounded-md">
            {currentAgency.id}
          </p>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">
            Created
          </label>
          <p className="text-xs text-muted-foreground">
            {new Date(currentAgency.created_at).toLocaleDateString()}
          </p>
        </div>

        <button
          onClick={() => update.mutate()}
          disabled={!name.trim() || name === currentAgency.name || update.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-sm hover:bg-accent/90 disabled:opacity-50"
        >
          {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save Changes
        </button>
      </div>

      {currentWorkspace && (
        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent" />
            Approval policy — {currentWorkspace.name}
          </h3>
          <label className="flex items-start gap-3 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={requireApproval}
              disabled={toggleApproval.isPending}
              onChange={(e) => toggleApproval.mutate(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="text-sm text-foreground block">Require approval before publishing</span>
              <span className="text-xs text-muted-foreground block mt-0.5">
                Posts created by editors are held for an owner or admin to approve before they can
                publish. Publish-now is converted into a review request.
              </span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
